import classNames from 'classnames'
import React, { useEffect, useRef } from 'react'
import { MouseEventHandler, TouchEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NavigateBeforeRounded, NavigateNextRounded } from '@mui/icons-material'
import { Box, IconButton } from '@mui/material'

import './MultiSlider.scss'

// Constants
const RESIZE_THRESHOLD = 1
const MOVEMENT_THRESHOLD = 10
const AUTO_BROWSE_INTERVAL_MS = 5000

/**
 * General purpose carousel / slider
 */
export type MultiSliderProps = {
  count: number
  padding: number
  children: React.ReactElement[]
  showIndex: boolean
  screenshotIndex?: number
  infinite?: boolean
  floatingButtons?: boolean
  browseAutomatically?: boolean
  restrictedWidth?: string | boolean
  /**
   * Accessible name for the carousel landmark. When provided the slider becomes a labelled
   * `role="region" aria-roledescription="carousel"`, which lets screen-reader users locate it
   * via landmark navigation. Omit to keep the legacy (unlabelled) behavior for existing callers.
   */
  ariaLabel?: string
  /** Accessible name for the previous-slide button. Defaults to `common:label_previous`. */
  ariaPrevLabel?: string
  /** Accessible name for the next-slide button. Defaults to `common:label_next`. */
  ariaNextLabel?: string
  /**
   * When true, overlays the "n / total" index as a pill in the slider's bottom-center
   * instead of stacking it below the viewport. Pairs naturally with `floatingButtons`
   * to keep the slider chrome attached to the image. Default (false) preserves the
   * legacy footer-below-the-image layout that other consumers rely on.
   */
  floatingIndex?: boolean
  /**
   * Height (px) of just the image/media portion of each slide, when a slide also renders
   * non-media content below it (e.g. a source caption). `floatingButtons`/`floatingIndex`
   * default to centering/anchoring against the *whole* slide height, which then floats over
   * the caption instead of the image once a slide is taller than its media. Pass the media's
   * own height here to anchor the floating chrome to the image regardless of what follows it.
   * Irrelevant when a slide's only content is its media (the default already matches that case).
   */
  mediaHeight?: number
}

const MultiSlider: React.FC<MultiSliderProps> = ({
  count,
  padding,
  children,
  showIndex,
  screenshotIndex = 0,
  infinite,
  floatingButtons,
  browseAutomatically,
  restrictedWidth,
  ariaLabel,
  ariaPrevLabel,
  ariaNextLabel,
  floatingIndex,
  mediaHeight,
}) => {
  const { t } = useTranslation()
  const viewPortRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(screenshotIndex)
  const [movingStarted, setMovingStarted] = useState(false)
  const [moving, setMoving] = useState(false)
  const [moveStart, setMoveStart] = useState(0)
  const [moveEnd, setMoveEnd] = useState(0)
  const resolvedCount = count > children.length ? children.length : count
  const resolvedRestrictedWidth = restrictedWidth === true ? '50%' : restrictedWidth ?? undefined
  // State to track automatic browsing
  const [width, setWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const containerWidth = window.innerWidth
      // `Math.ceil`, not the raw division: see the ResizeObserver effect below for why.
      return Math.ceil(containerWidth / resolvedCount - 2 * padding)
    }
    return 0
  })
  const [autoBrowseEnabled, setAutoBrowseEnabled] = useState(browseAutomatically)
  // The initial `width` is a `window.innerWidth`-based guess that rarely matches
  // the real container, so the first ResizeObserver measurement (below) corrects
  // it. `ready` gates the slide transition off until that correction has painted,
  // so the slider doesn't animate from the guessed width into its measured
  // position on load. Slide-switch transitions resume once ready.
  const [ready, setReady] = useState(false)

  // Enable transitions two frames after mount (and after any visible-slide-count
  // change, which recalculates the width — e.g. a gallery flipping from 4-up to
  // 2-up once image aspect ratios resolve). The double rAF puts the enable one
  // frame *past* the ResizeObserver's measured-width paint, so the correction
  // itself is instant. Kept in its own effect (not tied to `width`) so a width
  // update doesn't tear it down and cancel the pending enable.
  useEffect(() => {
    setReady(false)
    let enableFrame = 0
    const settleFrame = requestAnimationFrame(() => {
      enableFrame = requestAnimationFrame(() => setReady(true))
    })
    return () => {
      cancelAnimationFrame(settleFrame)
      cancelAnimationFrame(enableFrame)
    }
  }, [resolvedCount])

  useEffect(() => {
    if (!viewPortRef.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      // `entry.contentRect.width`, not `clientWidth`: `clientWidth` rounds the viewport's real
      // (sub-pixel) layout width down to a whole number before we ever see it — e.g. an actual
      // 328.33px viewport (a `1fr` CSS Grid column rarely lands on an integer) reports 328.
      // `contentRect` carries the un-rounded value, which `Math.ceil` below then rounds up
      // instead of down. Every slide's own width and the per-index translate step both derive
      // from this number, so rounding down left each slide a hair narrower than its real slot:
      // the viewport's actual clip boundary sat past the slide's right edge, letting a sliver of
      // the next slide peek through — reproducible only at the specific window widths where the
      // real layout width happens to fall on a fraction. Rounding up instead guarantees each
      // slide's box covers its whole real slot; the imperceptible sub-pixel overhang that
      // creates is clipped by the viewport's own `overflow: hidden`, so nothing is visibly
      // cropped.
      const measuredWidth = entries[0]?.contentRect.width ?? viewPortRef.current?.clientWidth
      if (measuredWidth) {
        const newWidth = Math.ceil(measuredWidth / resolvedCount - 2 * padding)
        // `>=`, not `>`: a resize that nudges the rounded width by exactly `RESIZE_THRESHOLD`
        // (1px) used to fail this check and get silently dropped, leaving `width` stale until a
        // later resize moved it by 2px or more in one tick.
        if (Math.abs(newWidth - width) >= RESIZE_THRESHOLD) {
          // Only update if change is significant
          setWidth(newWidth)
        }
      }
    })
    resizeObserver.observe(viewPortRef.current)
    return () => resizeObserver.disconnect()
  }, [padding, resolvedCount, width])

  const handleTouchStart: TouchEventHandler = (e) => {
    e.preventDefault()
    handleStart(e.targetTouches[0].clientX)
  }
  const handleTouchMove: TouchEventHandler = (e) => {
    handleMove(e.targetTouches[0].clientX)
  }
  const handleMouseDown: MouseEventHandler = (e) => {
    e.preventDefault()
    handleStart(e.clientX)
  }
  const handleMouseMove: MouseEventHandler = (e) => {
    handleMove(e.clientX)
  }
  const handleMouseEnd: MouseEventHandler = (e) => {
    e.preventDefault()
    handleEnd()
  }

  const handleStart = (x: number) => {
    setMovingStarted(true)
    setMoveStart(x)
    setAutoBrowseEnabled(false)
  }

  const handleMove = (x: number) => {
    if (movingStarted && Math.abs(x - moveStart) > MOVEMENT_THRESHOLD) {
      setMoving(true)
      setMoveEnd(x)
    }
  }

  const handleEnd = () => {
    setMovingStarted(false)
    if (moving) {
      setMoving(false)
      const change = Math.round((moveEnd - moveStart) / (width + 2 * padding))
      setIndex(Math.min(Math.max(0, index - change), children.length - resolvedCount))
    }
  }

  useEffect(() => {
    let intervalId: NodeJS.Timeout

    const startAutoBrowse = () => {
      intervalId = setInterval(() => {
        if (!moving && !movingStarted) {
          setIndex((prevIndex) => (infinite && prevIndex === children.length - resolvedCount ? 0 : prevIndex + 1))
        }
      }, AUTO_BROWSE_INTERVAL_MS)
    }

    // Start automatic browsing if enabled
    if (autoBrowseEnabled) {
      startAutoBrowse()
    }

    return () => {
      // Clear interval on component unmount
      clearInterval(intervalId)
    }
  }, [autoBrowseEnabled, moving, movingStarted, infinite, children.length, resolvedCount])

  const allItemsFitInView = children.length <= resolvedCount
  const prevAvailable = index > 0
  const nextAvailable = index < children.length - resolvedCount
  const buttonClassNames = classNames({ floating: floatingButtons })
  const disabledClassNames = classNames({ disabled: allItemsFitInView || ((!prevAvailable || !nextAvailable) && !infinite && infinite !== undefined) })

  return (
    <div
      className="MultiSlider"
      role={ariaLabel ? 'region' : undefined}
      aria-roledescription={ariaLabel ? 'carousel' : undefined}
      aria-label={ariaLabel}
      style={mediaHeight ? ({ '--media-height': `${mediaHeight}px` } as React.CSSProperties) : undefined}
    >
      <div className="slider">
        <IconButton
          className={`naviButton prev ${buttonClassNames} ${disabledClassNames}`}
          disabled={allItemsFitInView || (!prevAvailable && !infinite)}
          aria-label={ariaPrevLabel ?? t('common:label_previous')}
          onClick={() => {
            setAutoBrowseEnabled(false)
            if (infinite && index === 0) {
              setIndex(children.length - resolvedCount)
            } else {
              setIndex(index - 1)
            }
          }}
        >
          <NavigateBeforeRounded />
        </IconButton>

        <div className="viewport" ref={viewPortRef}>
          <div
            className="items"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseEnd}
            onMouseUp={handleMouseEnd}
            style={{
              width: children.length > 1 ? children.length * (width + 2 * padding) : resolvedRestrictedWidth ? resolvedRestrictedWidth : width + 2 * padding,
              left: `calc(${index * -(width + 2 * padding)}px + ${moving ? moveEnd - moveStart : 0}px)`,
              transition: moving || !ready ? 'none' : '1s',
              animation: moving || !ready ? 'none' : `slide 1s forwards`,
            }}
          >
            {children.map((child, i) => {
              // Slides clipped by the viewport stay out of the tab order and the
              // accessibility tree; focusing one would also scroll the clipped
              // viewport out of step with `index`.
              const offScreen = i < index || i >= index + resolvedCount
              return React.cloneElement(child, {
                key: i,
                className: `item ${child.props.className ? ' ' + child.props.className : ''}`,
                'aria-hidden': offScreen || undefined,
                // React 18 has no `inert` prop; an empty string sets the attribute.
                inert: offScreen ? '' : undefined,
                style: {
                  width,
                  padding: `0px ${padding}px`,
                  pointerEvents: moving ? 'none' : 'auto',
                  ...child.props.style,
                },
              })
            })}
          </div>
        </div>

        <IconButton
          className={`naviButton next ${buttonClassNames} ${disabledClassNames}`}
          disabled={allItemsFitInView || (!nextAvailable && !infinite)}
          aria-label={ariaNextLabel ?? t('common:label_next')}
          onClick={() => {
            setAutoBrowseEnabled(false)
            if (infinite && index === children.length - resolvedCount) {
              setIndex(0)
            } else {
              setIndex(index + 1)
            }
          }}
        >
          <NavigateNextRounded />
        </IconButton>
      </div>
      {showIndex && (
        // Announces the position only once the reader drives the slider: while it
        // browses itself, every auto-advance would interrupt a screen reader.
        <Box
          className={`footer${floatingIndex ? ' footer--floating' : ''}`}
          mt={floatingIndex ? 0 : 2}
          role={autoBrowseEnabled ? undefined : 'status'}
          aria-live={autoBrowseEnabled ? 'off' : 'polite'}
          aria-atomic="true"
        >
          {count > 1 && (
            <span className="index">
              {index + 1} - {index + resolvedCount} / {children.length}
            </span>
          )}
          {count === 1 && (
            <span className="index">
              {index + 1} / {children.length}
            </span>
          )}
        </Box>
      )}
    </div>
  )
}

export default MultiSlider
